import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, PlayCircle } from "lucide-react";

export default function SimulateFixtureModal({ open, onOpenChange, onSuccess }) {
  const [fixtures, setFixtures] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/peppol/fixtures")
      .then(({ data }) => {
        setFixtures(data.fixtures || []);
        if (data.fixtures?.[0]?.name) setSelected(data.fixtures[0].name);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const simulate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const nameNoExt = selected.replace(/\.xml$/i, "");
      const { data } = await api.post(
        `/admin/peppol/inbound/simulate?fixture=${encodeURIComponent(nameNoExt)}`,
        {}
      );
      toast.success(`Facture ${data.invoice_id.slice(0, 8)}… reçue`);
      onSuccess?.(data.invoice_id);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-slate-800 bg-slate-900 text-slate-100" data-testid="simulate-fixture-modal">
        <DialogHeader>
          <DialogTitle>Simuler une réception Peppol</DialogTitle>
          <DialogDescription className="text-slate-400">
            MOCKED — les fixtures reproduisent des UBL BIS 3.0 réels que Flowin nous enverra une fois branché.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="text-xs text-slate-400">Choisir une fixture</Label>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          ) : (
            <div className="space-y-2" data-testid="fixture-list">
              {fixtures.map((f) => (
                <label
                  key={f.name}
                  className={`flex cursor-pointer items-start gap-3 rounded border p-3 text-sm ${
                    selected === f.name
                      ? "border-[#C4553B] bg-[#C4553B]/10"
                      : "border-slate-700 bg-slate-950/50 hover:bg-slate-800"
                  }`}
                >
                  <input
                    type="radio"
                    name="fixture"
                    value={f.name}
                    checked={selected === f.name}
                    onChange={() => setSelected(f.name)}
                    className="mt-0.5"
                    data-testid={`fixture-radio-${f.name}`}
                  />
                  <div>
                    <div className="font-medium">{f.label}</div>
                    <div className="text-xs text-slate-500">{f.name}</div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 bg-slate-800 text-slate-200">
            Annuler
          </Button>
          <Button
            onClick={simulate}
            disabled={busy || !selected}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="simulate-fixture-submit"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-1 h-4 w-4" />}
            Simuler la réception
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
